// Casing / open-hole geometry resolution — ONE place that decides which
// diameter an interval contributes, and where that number came from.
//
// Source precedence (identical everywhere, never inverted):
//   1. explicit MANUAL override entered on the record;
//   2. the linked Reference Library value, when no override exists;
//   3. nothing — reported as missing. Never fabricated, never zeroed.
//
// An INVALID manual override is a validation error, not a reason to fall
// back to the library: silently using a different number than the one on
// screen would be worse than refusing to compute.
//
// Nominal casing size is deliberately NOT accepted as a substitute for
// either the casing internal diameter or the open-hole diameter — it is a
// label (e.g. "9-5/8 in"), not a measured bore.

import { findById } from "@/lib/reflibs/lookup";

const _pos = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const _entered = (v) => v !== "" && v != null;

function _ref(refId) {
  if (!refId) return null;
  try { return findById(refId) || null; } catch { return null; }
}

/**
 * Resolve ONE casing-program row's inner / outer diameter.
 * Returns { idIn, idSource, odIn, odSource, invalid[], missing[], label }.
 */
export function resolveCasingRowGeometry(row) {
  const label = row?.holeSection || row?.nominalSize || "casing string";
  const out = {
    label,
    topFt:    row?.topDepthFt === "" || row?.topDepthFt == null ? null : Number(row.topDepthFt),
    bottomFt: _pos(row?.bottomDepthFt),
    idIn: null, idSource: "",
    odIn: null, odSource: "",
    invalid: [], missing: [],
  };
  if (!row) return out;

  const ref = _ref(row.casingRefId);

  // Inner diameter ---------------------------------------------------------
  if (_entered(row.manualInnerDiameterIn)) {
    const v = _pos(row.manualInnerDiameterIn);
    if (v == null) out.invalid.push(`${label}: manual casing ID "${row.manualInnerDiameterIn}" is not a positive number`);
    else { out.idIn = v; out.idSource = "manual"; }
  } else {
    const v = ref ? _pos(ref.innerDiameter) : null;
    if (v != null) { out.idIn = v; out.idSource = "reference library"; }
    else out.missing.push(`${label}: casing internal diameter`);
  }

  // Outer diameter (actual, not nominal) -----------------------------------
  if (_entered(row.manualOuterDiameterIn)) {
    const v = _pos(row.manualOuterDiameterIn);
    if (v == null) out.invalid.push(`${label}: manual casing OD "${row.manualOuterDiameterIn}" is not a positive number`);
    else { out.odIn = v; out.odSource = "manual"; }
  } else {
    const v = ref ? _pos(ref.outerDiameter) : null;
    if (v != null) { out.odIn = v; out.odSource = "reference library"; }
  }

  if (out.idIn != null && out.odIn != null && out.odIn <= out.idIn) {
    out.invalid.push(`${label}: casing OD ${out.odIn} in must be greater than its ID ${out.idIn} in`);
  }
  return out;
}

/**
 * Build the non-overlapping casing-ID intervals of a well, so each depth
 * interval is evaluated against the casing string that actually covers it
 * instead of applying the deepest string's ID to the whole hole.
 *
 * Returns { intervals: [{ topFt, bottomFt, idIn, idSource, label }],
 *           invalid: [], missing: [] }
 */
export function casingIdIntervals(well) {
  const rows = well?.children?.casingProgram || [];
  const invalid = [];
  const missing = [];
  const resolved = [];

  for (const row of rows) {
    const g = resolveCasingRowGeometry(row);
    invalid.push(...g.invalid);
    if (g.bottomFt == null) {
      if (g.idIn != null) missing.push(`${g.label}: bottom depth`);
      continue;
    }
    const top = Number.isFinite(g.topFt) && g.topFt > 0 ? g.topFt : 0;
    if (top >= g.bottomFt) continue;                // schema validation reports this
    if (g.idIn == null) { missing.push(...g.missing); continue; }
    resolved.push({ topFt: top, bottomFt: g.bottomFt, idIn: g.idIn, idSource: g.idSource, label: g.label });
  }

  // Split on every boundary, then pick the governing (innermost) string for
  // each slice: the deepest-set string wins; equal depths → smallest ID.
  const bounds = Array.from(new Set(resolved.flatMap((r) => [r.topFt, r.bottomFt]))).sort((a, b) => a - b);
  const intervals = [];
  for (let i = 0; i < bounds.length - 1; i++) {
    const top = bounds[i], bottom = bounds[i + 1];
    const mid = (top + bottom) / 2;
    const covering = resolved.filter((r) => r.topFt <= mid && r.bottomFt >= mid);
    if (covering.length === 0) continue;
    covering.sort((a, b) => (b.bottomFt - a.bottomFt) || (a.idIn - b.idIn));
    const pick = covering[0];
    const prev = intervals[intervals.length - 1];
    if (prev && prev.bottomFt === top && prev.idIn === pick.idIn && prev.label === pick.label) {
      prev.bottomFt = bottom;                       // merge adjacent identical slices
    } else {
      intervals.push({ topFt: top, bottomFt: bottom, idIn: pick.idIn, idSource: pick.idSource, label: pick.label });
    }
  }
  return { intervals, invalid, missing };
}

/**
 * Open-hole (drilled) diameter — the diameter of the hole below the shoe.
 *
 *   1. the Well Profile's manual open-hole diameter override;
 *   2. the bound BHA's bit size;
 *   3. unknown (reported).
 *
 * Nominal casing size is NOT used here — a 9-5/8 in casing string is not a
 * 9-5/8 in open hole.
 */
export function resolveOpenHoleDiameterIn({ well, bha } = {}) {
  if (_entered(well?.openHoleDiameterIn)) {
    const v = _pos(well.openHoleDiameterIn);
    if (v == null) {
      return { value: null, source: "", invalid: `manual open-hole diameter "${well.openHoleDiameterIn}" is not a positive number` };
    }
    return { value: v, source: "manual", invalid: "" };
  }
  const bit = _pos(bha?.bitSize);
  if (bit != null) return { value: bit, source: "bha bit size", invalid: "" };
  return { value: null, source: "", invalid: "" };
}
